import { useMutation, useQuery } from '@apollo/client';
import { useCustomSnackbar, useUuid } from '@procureai/ui-mui';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { I18nNamespace } from '@/legacy/common/types';
import { ChatMessageProps } from '@/legacy/common/types/ChatMessage';
import { ChatActionNext } from '@/legacy/components/ChatStep/ChatActions/ChatActionNext';
import { ChatStep } from '@/legacy/components/ChatStep/ChatStep';
import { CircularProgressCentered } from '@/legacy/components/CircularProgressCentered/CircularProgressCentered';
import { useChatSession } from '@/legacy/modules';

import { IntakeNuclearComplianceForm } from './components/IntakeNuclearComplianceForm';
import { INTAKE_NUCLEAR_COMPLIANCE_FORM_MUTATION } from './IntakeNuclearComplianceFormStep.mutation.gql';
import { INTAKE_NUCLEAR_COMPLIANCE_FORM_QUERY } from './IntakeNuclearComplianceFormStep.query.gql';
import { intakeNuclearComplianceFormStepSubmitSchema } from './IntakeNuclearComplianceFormStep.schema';

export type IntakeNuclearComplianceFormStepProps = ChatMessageProps;

/**
 * This is a dedicated compliance form step for the nuclear intake flow.
 * This is mainly a list of checkboxes that appear depending on the yes/no selection of previous questions and submits that as a generic JSON format to the backend.
 */
export const IntakeNuclearComplianceFormStep = ({
  role,
  createdBy,
  chatSessionCompleted,
}: IntakeNuclearComplianceFormStepProps): ReactNode => {
  const { t: tIntake } = useTranslation(I18nNamespace.intake);
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'IntakeNuclearComplianceFormStep' });
  const { onError } = useCustomSnackbar();
  const session = useChatSession();
  const [isFormValid, setIsFormValid] = useState(false);

  const formId = useUuid();

  const { data, loading } = useQuery(INTAKE_NUCLEAR_COMPLIANCE_FORM_QUERY, {
    variables: { id: session.id },
  });

  const [submitData, { loading: submitting }] = useMutation(INTAKE_NUCLEAR_COMPLIANCE_FORM_MUTATION);

  // get current form data and run through schema to ensure correct format
  const currentData = data?.sevenbridges.chatSession?.request?.complianceFormData;
  const parsed = currentData ? intakeNuclearComplianceFormStepSubmitSchema.safeParse(currentData) : null;
  const parsedData = parsed?.success ? parsed.data : null;

  return (
    <ChatStep
      title={tIntake('IntakeChatStep.assistant')}
      role={role}
      createdBy={createdBy}
      message={t('message')}
      footer={
        <ChatActionNext
          formId={formId}
          disabled={chatSessionCompleted || loading || submitting || !isFormValid}
          loading={submitting}
        />
      }
    >
      {loading ? (
        <CircularProgressCentered />
      ) : (
        <IntakeNuclearComplianceForm
          formId={formId}
          data={parsedData ?? null}
          onValidityChange={setIsFormValid}
          onSubmit={async (data) => {
            const parsedSubmit = intakeNuclearComplianceFormStepSubmitSchema.safeParse(data);
            if (!parsedSubmit.success) return;

            await submitData({
              variables: {
                chatSessionId: session.id,
                data: parsedSubmit.data,
              },
            }).catch(onError);
          }}
        />
      )}
    </ChatStep>
  );
};
